import debouncer from "../Utilities/debouncer";
import { createSpinner, createTdWithElement, createTdWithText, createImg, createTableBodyLoadingIndicator, createCheckedIcon, createIcon } from "../DomUtilities/ElementsCreator";
import { insertBody } from "../DomUtilities/ElementsUtil";
import ITableSort from "../Sorting/ITableSort";
import ITablePaginator from "../Pagination/ITablePaginator";
import TablePaginator from "../Pagination/TablePaginator";
import TableSort from "../Sorting/TableSort";
import PageModel from "../Models/ViewModels/PageModel";
import { fetchStoresList } from "../HTTPRequests/StoreRequests";
import openErrorModal from "../Error/ErrorModal";
import { getProductionFacilityNames } from "../HTTPRequests/ProductionFacilityRequests";
import CreateStoreModal from "./CreateStoreModal";
import UpdateStoreModal from "./UpdateStoreModal";
import Mediator from "../Utilities/Mediator";
import { DeliveryMethod } from "../Models/DeliveryMethod";
import StoreViewModel from "../Models/ViewModels/StoreViewModel";
import ResendStoreEmailModal from "./ResendStoreEmailModal";

export default class Stores implements ITableSort, ITablePaginator {
	table: HTMLTableElement;
	searchBar: HTMLInputElement;
	paginator: TablePaginator;
	createBtn: HTMLButtonElement;
	searchValue: string;
	sortHandler: TableSort;
	productionFacilityTds: HTMLTableCellElement[];
	productionFacilityIds: Set<string>;
	resendEmailModal: HTMLDivElement;

	constructor() {
		this.table = document.querySelector('#stores');
		this.searchBar = document.querySelector('input.search-bar');
		this.createBtn = document.querySelector('.create-btn');
		this.resendEmailModal = document.querySelector('#resend-email-modal');
		this.searchValue = this.SearchValue;
		this.paginator = new TablePaginator(this);
		this.sortHandler = new TableSort(this);

		this.init();
	}

	private init(): void {
		Mediator.on("storeCrudOperationFinished", () => this.fetchList());
		this.fetchList();
		this.addSearchInputListener();
		this.sortHandler.addSortingIconsClickListeners();
		this.paginator.addPaginationEventsListeners();
		new CreateStoreModal(this.createBtn).addCreateModalClickListener();
	}

	public fetchList() {
		this.showLoadingIndicator();
		fetchStoresList(this.SearchValue, this.sortHandler.sortValue, this.sortHandler.sortAscending, this.paginator.currentPage, this.paginator.pageSize)
			.then((response: PageModel<StoreViewModel>) => {
				this.updateTable(response);
			}, () => { //backend error
				openErrorModal();
			}).catch(() => { //frontend error
				openErrorModal();
			});
	}

	private showLoadingIndicator() {
		insertBody(this.table, createTableBodyLoadingIndicator());
	}

	private updateTable(data: PageModel<StoreViewModel>) {
		this.paginator.currentPage = data.pageNumber;
		this.paginator.total = data.total;
		this.paginator.pageSize = data.pageSize;
		this.renderTable(data.list);
	}

	private renderTable(list: Array<StoreViewModel>) {
		insertBody(this.table,
			this.renderBody(list)
		);
		this.paginator.renderFoot();
	}

	private renderBody(storesList: Array<StoreViewModel>) {
		const tbody = document.createElement('tbody');
		if (storesList.length) {
			this.productionFacilityTds = [];
			this.productionFacilityIds = new Set<string>();

			for (const store of storesList) {
				const row = this.createStoreTableRow(store);
				row.onclick = () => {
					new UpdateStoreModal(row);
				}
				tbody.append(row);
			}

			if (this.productionFacilityIds.size) {
				this.showProductionFacilities();
			}
		} else {
			tbody.append(this.createEmptyListTableRow());
		}

		return tbody;
	}

	private createStoreTableRow(store: StoreViewModel): HTMLTableRowElement {
		const tr = document.createElement('tr');
		tr.id = store.id;
		tr.classList.add('table__row');

		let tdName: HTMLTableCellElement;
		if (store.eConomicCustomerId) {
			tdName = createTdWithElement(this.createEconomicTootip(store));
		} else {
			tdName = createTdWithText(store.name);
		}

		tr.append(tdName);
		const tdCVR = createTdWithText(store.cvr);
		tr.append(tdCVR);
		const tdPhoneNumber = createTdWithText(store.phoneNumber);
		tr.append(tdPhoneNumber);
		const tdEmail = createTdWithText(store.email);
		tr.append(tdEmail);
		const tdAddress = createTdWithText(store.address);
		tr.append(tdAddress);

		const tdProductionFacility = document.createElement('td');
		if (store.productionFacilityId) {
			tdProductionFacility.dataset.productionFacilityId = store.productionFacilityId;
			tdProductionFacility.append(createSpinner());
			this.productionFacilityTds.push(tdProductionFacility);
			this.productionFacilityIds.add(store.productionFacilityId);
		}
		tr.append(tdProductionFacility);

		const tdDelivery = document.createElement('td');
		if (store.deliveryMethod == DeliveryMethod.Mail) {
			tdDelivery.appendChild(createCheckedIcon());
		}
		tr.append(tdDelivery);

		const tdStraussStore = document.createElement('td');
		if (store.isDomainStore) {
			tdStraussStore.appendChild(createCheckedIcon());
		}
		tr.append(tdStraussStore);

		const imgEdit = createImg('/assets/edit.svg', 'edit icon');
		const resendIcon = createIcon("icon-envelope");
		resendIcon.title = "QR-code PDF";
		const lastTd = createTdWithElement(resendIcon, imgEdit);
		this.createResendClickListener(resendIcon, store);

		tr.append(lastTd);

		return tr;
	}

	private createEconomicTootip(store: StoreViewModel): HTMLDivElement {
		const infoIcon = createImg('/assets/info.svg', 'economic-info');
		const imgEconomic = createImg('/assets/Visma-e-conomic-logo.png', 'economic-logo');
		const economicInfoWrapper: HTMLDivElement = document.createElement("div");
		const economicLogoWrapper: HTMLDivElement = document.createElement("div");
		const downArrowIcon: HTMLElement = createIcon('icon-caret-down');
		const economicInfo: HTMLParagraphElement = document.createElement("p");
		const storeName: HTMLSpanElement = document.createElement("span");

		storeName.innerHTML = store.name;
		economicInfo.innerHTML = `${this.table.dataset.economicmessage} ${store.eConomicCustomerId}`;

		economicLogoWrapper.append(imgEconomic);
		economicLogoWrapper.append(economicInfo);
		economicLogoWrapper.append(downArrowIcon);

		economicInfoWrapper.append(infoIcon);
		economicInfoWrapper.append(storeName);
		economicInfoWrapper.append(economicLogoWrapper);

		return economicInfoWrapper;
	}

	private createResendClickListener(icon: HTMLElement, store: StoreViewModel) {
		icon.onclick = (event) => {
			event.stopPropagation();
			new ResendStoreEmailModal(store, this.resendEmailModal);
		}
	}

	private showProductionFacilities() {
		getProductionFacilityNames([...this.productionFacilityIds])
			.then(result => {
				for (const td of this.productionFacilityTds) {
					td.innerHTML = "";
					td.innerText = result.get(td.dataset.productionFacilityId) || "";
				}
			}, () => { // backend error
				this.productionFacilityTds.forEach(td => td.innerHTML = "");
			}).catch(() => { // frontend error
				this.productionFacilityTds.forEach(td => td.innerHTML = "");
			});
	}

	private createEmptyListTableRow(): HTMLTableRowElement {
		const emptyText = this.table.dataset.empty;
		const tr = document.createElement('tr');
		const td = createTdWithText(emptyText);
		td.colSpan = this.table.querySelectorAll('th').length;
		td.style.textAlign = 'center';
		tr.append(td);

		return tr;
	}

	private get SearchValue(): string {
		return this.searchBar.value.trim();
	}

	private addSearchInputListener() {
		const debounce = debouncer(this.searchList.bind(this), 400, false);

		this.searchBar.oninput = (event) => {
			if ((event.target["value"]).trim() != this.searchValue) {
				debounce(event);
			}
		}
	}

	private searchList() {
		this.searchValue = this.SearchValue;
		this.fetchList();
	}
}
